import dayjs from 'dayjs'
import { getConfigOptions } from '../../pages/Statistics/utils'
import { resolveOptionDisplayLabel } from '../../utils/config-localization'
import {
  DEFAULT_OPTION_ICON_FILENAME,
  defaultIconFilenameForKind,
  resolveOptionIconFilename,
} from '../../utils/config-option-icons'

/** Injury severity su persona (allineato a constants SEVERITIES). */
export const PERSON_INJURY_NO_INJURY = 1
export const PERSON_INJURY_SLIGHT = 2
export const PERSON_INJURY_SERIOUS = 3
export const PERSON_INJURY_FATAL = 4

export const ORG_POLICE = 1
export const ORG_HEALTHCARE = 2

/** Colori icona persona per gravità (mockup popup mappa). */
export const PERSON_INJURY_COLORS = {
  fatal: '#D50000',
  serious: '#EF6C00',
  slight: '#FFD600',
  none: '#4CAF50',
  unknown: '#94A3B8',
}

/**
 * @param {object} crash
 * @param {Array<object>} [organizations]
 * @param {{ mergedCompact?: string, validatedExpanded?: string, expanded?: boolean }} [labels]
 */
export function getCrashReportTypeLabel(
  crash,
  organizations = [],
  labels = {},
) {
  const {
    mergedCompact = 'Merged/Validated',
    validatedExpanded = 'Validated',
    expanded = false,
  } = labels

  if (crash?.merge_date) {
    return expanded ? validatedExpanded : mergedCompact
  }

  const org = organizations?.find((o) => Number(o.id ?? o.value) === Number(crash?.organization))
  let label = org?.description ?? org?.label ?? String(crash?.organization ?? '')
  if (label && !/\(final\)/i.test(label)) {
    label = `${label} (Final)`
  }
  return label
}

/**
 * Report Healthcare (Final): popup espanso senza sezione veicoli/persone.
 * @param {object} crash
 */
export function isHealthcareSummaryOnlyReport(crash) {
  return Number(crash?.organization) === ORG_HEALTHCARE && !crash?.merge_date
}

/**
 * Mostra VIEW per Validated; EDIT solo se l'utente può modificare.
 * @param {object} user
 * @param {object} crash
 * @param {boolean} isMobile
 */
export function canShowMapPopupAction(user, crash, isMobile) {
  if (!crash) return false
  if (crash.merge_date) return true
  return hasCrashMapContentPermission(user, crash, isMobile)
}

/**
 * @param {unknown} injurySeverity
 */
export function getPersonInjuryColor(injurySeverity) {
  const severity = Number(injurySeverity)
  if (severity === PERSON_INJURY_FATAL) return PERSON_INJURY_COLORS.fatal
  if (severity === PERSON_INJURY_SERIOUS) return PERSON_INJURY_COLORS.serious
  if (severity === PERSON_INJURY_SLIGHT) return PERSON_INJURY_COLORS.slight
  if (severity === PERSON_INJURY_NO_INJURY) return PERSON_INJURY_COLORS.none
  return PERSON_INJURY_COLORS.unknown
}

/**
 * Map popup vehicle icon from Unit Type **label** (structure.json may differ per market).
 * @param {string} label
 * @returns {'car' | 'van' | 'bus' | 'moped' | 'cyclist' | 'pedestrian' | 'ridden-animal' | 'tractor' | null}
 */
function matchVehicleIconFromLabel(label) {
  const normalized = String(label ?? '').trim().toLowerCase()
  if (!normalized || normalized === 'unknown') return null

  if (/\bpedestrian\b|\bpedon/.test(normalized)) return 'pedestrian'
  if (/\bbicycle\b|\bbicycl|\bcyclist\b|pedal cycle|pedal.cycle/.test(normalized)) return 'cyclist'
  if (/\bmoped\b|\bcyclomoteur\b|motorino/.test(normalized)) return 'moped'
  if (/\belectric or kick scooter\b|\bscooter\b/.test(normalized)) return 'moped'
  if (/\bmotorcycle\b|\bmotocyc/.test(normalized)) return 'motorcycle'
  if (/\bridden animal\b|horse.?rider|equestrian/.test(normalized)) return 'ridden-animal'
  if (/three wheeler|tuk.?tuk/.test(normalized)) return 'car'
  if (/\bbus\b|trolley|tram/.test(normalized)) return 'bus'
  if (/minibus|goods vehicle|road tractor|van|truck|lorry|heavy|commercial|delivery/.test(normalized)) {
    return 'van'
  }
  if (/agricultural tractor|farm tractor|\btractor\b/.test(normalized)) return 'tractor'
  if (/\bcar\b|other light vehicle|other motor vehicle/.test(normalized)) return 'car'

  return null
}

/**
 * @param {Record<string, unknown>} unit
 * @param {Record<string, Array<{ value: unknown, label: string }>>} configOptions
 * @returns {'car' | 'van' | 'bus' | 'moped' | 'cyclist' | 'pedestrian' | 'ridden-animal' | 'tractor'}
 */
export function getVehicleIconKind(unit, configOptions) {
  const resolvedLabel = getVehicleTypeLabel(unit, configOptions)
  const fromResolvedLabel = matchVehicleIconFromLabel(resolvedLabel)
  if (fromResolvedLabel) return fromResolvedLabel

  const rawType = unit?.Type
  if (typeof rawType === 'string' && Number.isNaN(Number(rawType))) {
    const fromRawLabel = matchVehicleIconFromLabel(rawType)
    if (fromRawLabel) return fromRawLabel
  }

  return 'car'
}

/**
 * Icon for person row from Road user type **label** (injury color applied in UI).
 * @param {Record<string, unknown>} person
 * @param {Record<string, Array<{ value: unknown, label: string }>>} configOptions
 * @returns {'person' | 'cyclist' | 'pedestrian'}
 */
export function getPersonRoadUserIconKind(person, configOptions) {
  const label = getPersonRoadUserTypeLabel(person, configOptions)
  const normalized = String(label ?? '').trim().toLowerCase()
  if (!normalized || normalized === 'unknown') return 'person'
  if (/\bpedestrian\b|\bpedon/.test(normalized)) return 'pedestrian'
  if (/\bcyclist\b|\bbicycle\b|pedal cycle|pedal.cycle/.test(normalized)) return 'cyclist'
  return 'person'
}

/**
 * Configured SVG filename for unit Type (option.icon → label fallback).
 * @param {Record<string, unknown>} unit
 * @param {Record<string, Array<{ value: unknown, label: string, icon?: string }>>} configOptions
 * @returns {string | null}
 */
export function getVehicleIconFilename(unit, configOptions) {
  const fromOption = resolveOptionIconFilename(configOptions, 'Type', unit?.Type)
  if (fromOption) return fromOption
  return defaultIconFilenameForKind(getVehicleIconKind(unit, configOptions))
}

/**
 * Configured SVG filename for Road user type (option.icon → label fallback).
 * @param {Record<string, unknown>} person
 * @param {Record<string, Array<{ value: unknown, label: string, icon?: string }>>} configOptions
 * @returns {string | null}
 */
export function getPersonRoadUserIconFilename(person, configOptions) {
  const fromOption = resolveOptionIconFilename(
    configOptions,
    'Road user type',
    person?.['Road user type'],
  )
  if (fromOption && fromOption !== DEFAULT_OPTION_ICON_FILENAME) return fromOption
  return defaultIconFilenameForKind(getPersonRoadUserIconKind(person, configOptions))
}

/**
 * Icon for occupants inside a vehicle row (always person silhouette).
 * @returns {string}
 */
export function getUnitOccupantIconFilename() {
  return defaultIconFilenameForKind('person') ?? 'person.svg'
}

/**
 * Map popup person icon depends on row context (vehicle occupants vs road-level).
 * @param {Record<string, unknown>} person
 * @param {Record<string, Array<{ value: unknown, label: string, icon?: string }>>} configOptions
 * @param {string} rowKind
 * @returns {string | null}
 */
export function getMapPopupPersonIconFilename(person, configOptions, rowKind) {
  if (rowKind === MAP_POPUP_ROW_KIND.vehicle) {
    return getUnitOccupantIconFilename()
  }
  return getPersonRoadUserIconFilename(person, configOptions)
}

/** @returns {string} */
export function getRoadRowIconFilename() {
  return defaultIconFilenameForKind('road') ?? 'road.svg'
}

/**
 * @param {object} user
 * @param {object} crash
 * @param {boolean} isMobile
 */
export function hasCrashMapContentPermission(user, crash, isMobile) {
  if (!user || !crash) return false
  if (user.type === 1) return true
  if (user.type === 2 && Number(crash.organization) === Number(user.organization) && !isMobile) return true
  if (user.type === 3 && (user.organization_ids || []).includes(crash.organization) && !isMobile) return true
  if (crash.user_id === user.id) return true
  return false
}

/**
 * @param {unknown} person
 */
function isPersonRecord(person) {
  if (!person || typeof person !== 'object') return false
  return (
    person['Person id'] != null
    || person['Road user type'] != null
    || person['Injury severity'] != null
  )
}

/**
 * @param {Record<string, unknown>} person
 */
function getPersonIdKey(person) {
  const id = person?.['Person id']
  if (id == null || id === '') return null
  return String(id)
}

/**
 * One physical person may appear both at crash level and under a unit (or twice in merge payloads).
 * @param {Array<Record<string, unknown>>} people
 */
export function dedupePeopleById(people) {
  const seen = new Set()
  const result = []
  for (const person of people) {
    const idKey = getPersonIdKey(person)
    if (idKey != null) {
      if (seen.has(idKey)) continue
      seen.add(idKey)
    }
    result.push(person)
  }
  return result
}

/**
 * @param {object} crash
 * @returns {Array<Record<string, unknown>>}
 */
export function normalizeReducedCrashPeople(crash) {
  if (!crash) return []
  /** @type {Array<Record<string, unknown>>} */
  const people = []

  const pushPerson = (person) => {
    if (isPersonRecord(person)) people.push(person)
  }

  if (Array.isArray(crash.people)) {
    crash.people.forEach(pushPerson)
  } else if (crash.people && typeof crash.people === 'object') {
    pushPerson(crash.people)
  }

  for (const unit of normalizeReducedCrashUnits(crash)) {
    if (Array.isArray(unit.people)) {
      unit.people.forEach(pushPerson)
    } else if (unit.people) {
      pushPerson(unit.people)
    }
  }

  return dedupePeopleById(people)
}

/** Row kinds for map popup involved-parties list. */
export const MAP_POPUP_ROW_KIND = {
  vehicle: 'vehicle',
  road: 'road',
}

/**
 * @param {object} crash
 * @returns {Array<Record<string, unknown>>}
 */
function collectCrashLevelPeople(crash) {
  const rootPeople = []
  if (Array.isArray(crash?.people)) {
    crash.people.forEach((p) => { if (isPersonRecord(p)) rootPeople.push(p) })
  } else if (isPersonRecord(crash?.people)) {
    rootPeople.push(crash.people)
  }
  return dedupePeopleById(rootPeople)
}

/**
 * Person ids already shown under a vehicle unit.
 * @param {object} crash
 */
function collectUnitPeopleIdSet(crash) {
  const ids = new Set()
  for (const unit of normalizeReducedCrashUnits(crash)) {
    const people = []
    const push = (p) => { if (isPersonRecord(p)) people.push(p) }
    if (Array.isArray(unit.people)) unit.people.forEach(push)
    else if (unit.people) push(unit.people)
    for (const person of dedupePeopleById(people)) {
      const idKey = getPersonIdKey(person)
      if (idKey != null) ids.add(idKey)
    }
  }
  return ids
}

/**
 * Crash-level people not linked to any unit (pedestrians, hospital-only entries, etc.).
 * @param {object} crash
 */
export function getExternalCrashPeople(crash) {
  const unitIds = collectUnitPeopleIdSet(crash)
  return collectCrashLevelPeople(crash).filter((person) => {
    const idKey = getPersonIdKey(person)
    if (idKey == null) return true
    return !unitIds.has(idKey)
  })
}

/**
 * @param {object} crash
 * @returns {Array<Record<string, unknown>>}
 */
export function normalizeReducedCrashUnits(crash) {
  if (!crash) return []
  if (Array.isArray(crash.units) && crash.units.length > 0) {
    return crash.units.filter((u) => u && typeof u === 'object')
  }
  if (crash.unit && typeof crash.unit === 'object' && Object.keys(crash.unit).length > 0) {
    const hasUnitData =
      crash.unit.Type != null
      || crash.unit['Unit id'] != null
      || crash.unit['unit id'] != null
      || crash.unit.people
    if (hasUnitData) return [crash.unit]
  }
  return []
}

/**
 * @param {Array<Record<string, unknown>>} people
 */
export function computeVictimCounts(people) {
  let fatal = 0
  let serious = 0
  let slight = 0

  for (const person of people) {
    const severity = Number(person['Injury severity'])
    if (severity === PERSON_INJURY_FATAL) fatal += 1
    else if (severity === PERSON_INJURY_SERIOUS) serious += 1
    else if (severity === PERSON_INJURY_SLIGHT) slight += 1
  }

  return { fatal, serious, slight }
}

/**
 * @param {object} crash
 */
export function formatCrashDateTime(crash) {
  const raw = crash?.['Crash date'] ?? crash?.['Report date']
  if (!raw) return null
  const parsed = dayjs(raw)
  if (!parsed.isValid()) return String(raw)
  return parsed.format('DD/MM/YYYY - HH[h]mm')
}

/**
 * @param {unknown} value
 */
function normalizeLocationPart(value) {
  if (value == null) return null
  const text = String(value).trim()
  return text.length > 0 ? text : null
}

/**
 * Location line for map popup: road - city - state.
 * @param {object} crash
 * @param {Record<string, Array<{ value: unknown, label: string }>>} [configOptions]
 */
export function formatCrashLocation(crash, configOptions) {
  const road = normalizeLocationPart(crash?.road?.Road)
  const city = normalizeLocationPart(crash?.['City/Location'])
  const stateRaw = crash?.State
  const state = stateRaw != null && stateRaw !== ''
    ? normalizeLocationPart(
      resolveConfigOptionLabel(configOptions ?? {}, 'State', stateRaw) ?? stateRaw,
    )
    : null

  const parts = [road, city, state].filter(Boolean)
  return parts.length > 0 ? parts.join(' - ') : null
}

/**
 * @param {Record<string, Array<{ value: unknown, label: string }>>} configOptions
 * @param {string} fieldLabel
 * @param {unknown} value
 */
export function resolveConfigOptionLabel(configOptions, fieldLabel, value) {
  if (value == null || value === '') return null
  const options = configOptions?.[fieldLabel]
  if (!Array.isArray(options)) return String(value)
  const match = options.find(
    (opt) => opt.value === value || Number(opt.value) === Number(value),
  )
  if (match) return resolveOptionDisplayLabel(match)
  return String(value)
}

/**
 * @param {object} crash
 * @param {Record<string, Array<{ value: unknown, label: string }>>} configOptions
 */
export function getSpeedLimitDisplay(crash, configOptions) {
  const raw = crash?.road?.['Speed limit']
  if (raw == null || raw === '' || Number(raw) === 0) return null
  const label = resolveConfigOptionLabel(configOptions, 'Speed limit', raw)
  const numeric = String(label ?? '').match(/\d+/)
  return numeric ? numeric[0] : String(raw)
}

/**
 * @param {string | null | undefined} dateOfBirth
 */
export function computeAgeYears(dateOfBirth) {
  if (!dateOfBirth) return null
  const birth = dayjs(dateOfBirth)
  if (!birth.isValid()) return null
  const age = dayjs().diff(birth, 'year')
  return age >= 0 && age < 130 ? age : null
}

/**
 * Road user type label for map popup (privacy: no names).
 * @param {Record<string, unknown>} person
 * @param {Record<string, Array<{ value: unknown, label: string }>>} configOptions
 */
export function getPersonRoadUserTypeLabel(person, configOptions) {
  if (!person || typeof person !== 'object') return null
  const raw = person['Road user type']
  if (raw == null || raw === '') return null
  const label = resolveConfigOptionLabel(configOptions, 'Road user type', raw) ?? String(raw)
  const normalized = String(label).trim()
  if (!normalized || /^unknown$/i.test(normalized)) return null
  return normalized
}

/**
 * Seating position label for occupants (privacy-safe caption under person icon).
 * @param {Record<string, unknown>} person
 * @param {Record<string, Array<{ value: unknown, label: string }>>} configOptions
 */
export function getPersonSeatingPositionLabel(person, configOptions) {
  if (!person || typeof person !== 'object') return null
  const raw = person['Seating position in/on vehicle']
  if (raw == null || raw === '') return null
  const label = resolveConfigOptionLabel(
    configOptions,
    'Seating position in/on vehicle',
    raw,
  ) ?? String(raw)
  const normalized = String(label).trim()
  if (
    !normalized
    || /^unknown$/i.test(normalized)
    || /^not applicable$/i.test(normalized)
  ) {
    return null
  }
  return normalized
}

/**
 * @param {Record<string, unknown>} person
 * @param {Record<string, Array<{ value: unknown, label: string }>>} configOptions
 */
export function getPersonGenderKind(person, configOptions) {
  const raw = person.Gender
  const label = String(resolveConfigOptionLabel(configOptions, 'Gender', raw) ?? raw ?? '').toLowerCase()
  if (label.includes('female') || label.includes('woman') || label.includes('femme') || label === 'f') {
    return 'female'
  }
  if (label.includes('male') || label.includes('man') || label.includes('homme') || label === 'm') {
    return 'male'
  }
  return 'unknown'
}

/**
 * @param {Record<string, unknown>} unit
 * @param {Record<string, Array<{ value: unknown, label: string }>>} configOptions
 */
export function getVehicleTypeLabel(unit, configOptions) {
  if (!unit) return null
  return resolveConfigOptionLabel(configOptions, 'Type', unit.Type)
}

/**
 * Righe UI: veicolo + persone collegate; persone solo a livello crash → riga "road".
 * @param {object} crash
 * @returns {Array<{ unit: Record<string, unknown> | null, people: Array<Record<string, unknown>>, rowKind: string }>}
 */
export function buildUnitPeopleRows(crash) {
  const units = normalizeReducedCrashUnits(crash)
  const rows = []

  if (units.length > 0) {
    const externalPeople = getExternalCrashPeople(crash)
    if (externalPeople.length > 0) {
      rows.push({
        unit: null,
        people: externalPeople,
        rowKind: MAP_POPUP_ROW_KIND.road,
      })
    }

    for (const unit of units) {
      const people = []
      const push = (p) => { if (isPersonRecord(p)) people.push(p) }
      if (Array.isArray(unit.people)) unit.people.forEach(push)
      else if (unit.people) push(unit.people)
      const deduped = dedupePeopleById(people)
      if (unit || deduped.length > 0) {
        rows.push({ unit, people: deduped, rowKind: MAP_POPUP_ROW_KIND.vehicle })
      }
    }

    return rows
  }

  const rootPeople = collectCrashLevelPeople(crash)
  if (rootPeople.length > 0) {
    return [{
      unit: null,
      people: rootPeople,
      rowKind: MAP_POPUP_ROW_KIND.road,
    }]
  }
  return []
}

/**
 * Stable key for community map layer switches (popup closes when this changes).
 * @param {Array<{ name?: string, checked?: boolean }>} [communitySwitches]
 */
export function buildCommunitySwitchesKey(communitySwitches) {
  return (communitySwitches ?? [])
    .map((item) => `${item.name}:${Boolean(item.checked)}`)
    .join('|')
}

/**
 * @param {object} crash
 * @param {object} [configuration]
 * @param {Array<object>} [organizations]
 */
export function buildCrashMapDetailSummary(crash, configuration, organizations) {
  const configOptions = getConfigOptions(configuration ?? {}, organizations ?? [])
  const people = normalizeReducedCrashPeople(crash)
  const victimCounts = computeVictimCounts(people)
  const unitPeopleRows = buildUnitPeopleRows(crash)

  return {
    configOptions,
    people,
    victimCounts,
    dateTime: formatCrashDateTime(crash),
    location: formatCrashLocation(crash, configOptions),
    speedLimit: getSpeedLimitDisplay(crash, configOptions),
    unitPeopleRows,
    hasVictimStats: victimCounts.fatal + victimCounts.serious + victimCounts.slight > 0,
    hasInvolvedParties: unitPeopleRows.some((row) => row.people.length > 0),
  }
}
